/**
 * Navigation utilities for organization-based routing
 * Ensures no hardcoded org slugs and proper authentication flow
 */

import { appStore } from '../stores/AppStore';

export interface NavigationOptions {
  requireAuth?: boolean;
  fallbackToLogin?: boolean;
}

/**
 * Get the current organization slug from various sources
 * Priority: URL path > userStore > authStore
 */
export function getCurrentOrgSlug(): string | null {
  const { user, auth } = appStore;

  const pathParts = window.location.pathname.split('/');
  if (pathParts.length > 1 && pathParts[1] && !['login', 'signup', 'auth', 'public'].includes(pathParts[1])) {
    return decodeURIComponent(pathParts[1]);
  }

  if (user?.currentOrganization?.slug) {
    return user.currentOrganization.slug;
  }

  if (auth?.organization?.slug) {
    return auth.organization.slug;
  }

  // Org slug is managed via httpOnly cookies
  const storedOrgSlug = null;
  if (storedOrgSlug) return storedOrgSlug;

  return null;
}

/**
 * Build an organization-scoped path
 * Returns null if no org is available and throws or redirects based on options
 */
export function buildOrgPath(
  path: string, 
  options: NavigationOptions = { requireAuth: true, fallbackToLogin: true }
): string | null {
  const orgSlug = getCurrentOrgSlug();
  
  if (!orgSlug) {
    if (options.requireAuth) {
      console.error('No organization available for navigation');
      if (options.fallbackToLogin) {
        window.location.href = '/login';
      }
      return null;
    }
    return null;
  }
  
  // Ensure path starts with /
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `/${orgSlug}${cleanPath}`;
}

/**
 * Navigate to an organization-scoped route
 * Handles missing org gracefully
 */
export function navigateToOrgRoute(
  navigate: (path: string) => void,
  path: string,
  options: NavigationOptions = { requireAuth: true, fallbackToLogin: true }
): boolean {
  const fullPath = buildOrgPath(path, options);
  
  if (!fullPath) {
    if (options.fallbackToLogin) {
      navigate('/login');
    }
    // Org slug is managed via httpOnly cookies
    return false;
  }
  
  // Org slug is managed via httpOnly cookies
  navigate(fullPath);
  return true;
}

/**
 * Check if the current user has an organization
 */
export function hasOrganization(): boolean {
  const { user, auth } = appStore;
  return !!(user.currentOrganization?.id || auth.organization?.id);
}

/**
 * Get organization ID from current context
 */
export function getCurrentOrgId(): string | null {
  const { user, auth, organization } = appStore;
  
  return organization.organizationId || 
         user.currentOrganization?.id || 
         auth.organization?.id || 
         null;
}

/**
 * Ensure user is in a valid organization context
 * Redirects to login if not
 */
export function requireOrganization(navigate: (path: string) => void): boolean {
  if (!hasOrganization()) {
    console.error('No organization context available, redirecting to login');
    navigate('/login');
    // Org slug is managed via httpOnly cookies
    return false;
  }
  return true;
}
